import { Component, OnInit } from '@angular/core';
import { MessageBoxComponent } from '@common/popups/message-box/message-box.component';
import { LayoutService } from '@common/services/layout.service';
import { ModalService } from '@common/services/modal.service';
import { DatePickerHandler } from '@common/UI/date-picker/date-picker.component';
import { SortableExtractor } from '@common/UI/sortable/sortable.component';
import { Filterable } from '@common/utilities/Filterable';
import { commit, main } from '@core/operators';
import { ApiService } from '@core/services/api.service';
import { DataService, DateRange, ProductUnit, GoodReportDetail } from '@core/services/data.service';
import { PartnersService } from '@core/services/partners.service';
import { SynchronizationService } from '@core/services/synchronization.service';
import { TagsService } from '@core/services/tags.service';
import { Multilingual } from '@core/utilities/multilingual';
import { Storage } from '@core/utilities/storage';
import { ViewerComponent } from '@routes/invoices/viewer/viewer.component';
import { map } from 'rxjs/operators';
import { Report } from '../common/report';
import { ExportsViewerComponent } from './exports-viewer/exports-viewer.component';
import { ReturnsViewerComponent } from './returns-viewer/returns-viewer.component';

@Component({
  templateUrl: './inventory-report-by-imports.component.html',
  styleUrls: ['./inventory-report-by-imports.component.scss']
})
export class InventoryReportByImportsComponent implements OnInit {

  public get mode(): 'quantity' | 'worth' {
    return Storage.get(location.pathname, 'quantity');
  }

  public set mode(value: 'quantity' | 'worth') {
    Storage.set(location.pathname, value);
  }

  public paths: Report[] = [];
  public report: Report;

  constructor(private ds: DataService, private layout: LayoutService, private sync: SynchronizationService, private modal: ModalService, private partners: PartnersService, private tags: TagsService) {
  }

  public get loading(): boolean {
    return this.range != null && !this.sync.ready(InventoryReportByImportsComponent);
  }


  public total(type: 'Count' | 'Quantity' | 'Discount', ...reports: GoodReportDetail[]): number {
    return reports.filter(i => i !== undefined && i !== null).sum(i => i['Length' + type] + i['Weight' + type]);
  }

  public goods = new Filterable<Report>({
    extractor: i => [i.Path.join('.')],
    grouper: i => [
      { name: 'Theo trạng thái nhập', option: i.Imports.LengthCount > 0 ? 'Có nhập' : 'Không nhập' },
      { name: 'Theo trạng thái bán', option: i.Exports.LengthCount > 0 ? 'Có bán' : 'Không bán' },
      { name: 'Theo trạng thái trả', option: i.Returns.LengthCount > 0 ? 'Có trả' : 'Không trả' },
      { name: 'Theo trạng thái mất', option: i.CompanyLosses.LengthCount > 0 ? 'Có mất' : 'Không mất' },
      { name: 'Theo trạng thái tồn', option: 1 > 0 ? 'Có tồn' : 'Hết hàng' },
    ]
  });

  public selectMode() {
    this.modal.show(MessageBoxComponent, {
      state: {
        title: Multilingual.getText('Chọn hình thức hiển thị'),
        message: Multilingual.getText('Chọn một trong các hình thức hiển thị dữ liệu dưới đây') + ':',
        buttons: [
          { name: Multilingual.getText('Hiển thị theo số lượng hàng'), class: this.mode == 'quantity' ? '' : 'bg-primary text-white', click: () => this.mode = 'quantity', icon: this.mode == 'quantity' ? 'fa fa-check' : 'fa fa-boxes' },
          { name: Multilingual.getText('Hiện thị theo giá trị hàng'), class: this.mode == 'worth' ? '' : 'bg-primary text-white', click: () => this.mode = 'worth', icon: this.mode == 'worth' ? 'fa fa-check' : 'fa fa-coins' }
        ]
      }
    });
  }

  public get primaryLabel(): string {
    switch (this.paths.indexOf(this.report)) {
      case 0: return Multilingual.getText('Đơn hàng');
      case 1: return Multilingual.getText('Mã hàng');
      case 2: return Multilingual.getText('Mã màu');
    }
  }

  public extractor(field: string): SortableExtractor<Report> {
    return {
      extract: i => {
        switch (field) {
          case 'last-of-path': return i.Path.last();
          case 'initial': return i.base('initial', 'CNY');
          case 'initial-quantity': return i.Initial.LengthQuantity + i.Initial.WeightQuantity;
          case 'import': return i.base('import', 'CNY') - i.base('provider', 'CNY');
          case 'import-quantity': return i.Imports.LengthQuantity - i.ProviderLosses.LengthQuantity + i.Imports.WeightQuantity - i.ProviderLosses.WeightQuantity;
          case 'return': return i.quote('return', 'VND');
          case 'return-quantity': return i.Returns.LengthQuantity + i.Returns.WeightQuantity;
          case 'discount': return i.quote('discount', 'VND');
          case 'discount-quantity': return i.Exports.LengthDiscount + i.Exports.WeightDiscount;
          case 'lost': return i.quote('lost', 'VND');
          case 'lost-quantity': return i.CompanyLosses.LengthQuantity + i.CompanyLosses.WeightQuantity;
          case 'export': return i.quote('export', 'VND');
          case 'export-quantity': return i.Exports.LengthQuantity + i.Exports.WeightQuantity;
          case 'left': return i.base('left', 'CNY');
          case 'left-quantity': return i.left('quantity');
          case 'exchange': return i.exchange;
          case 'gross': return i.gross('total', 'VND');
          case '%': return i.base('profitable', 'CNY') != 0 ? i.gross('total', 'CNY') * 100 / i.base('profitable', 'CNY') : 0
        }
      }
    };
  }

  public range: { name: string, range: DateRange } = null;

  public handler: DatePickerHandler = {
    change: (info: { name: string, range: DateRange }) => {

      this.range = info;
      this.sync.notify(InventoryReportByImportsComponent, false);

      return this.ds.reports('goods-by-imports', { range: info.range }).pipe(map(data => {

        this.sync.notify(InventoryReportByImportsComponent, true);

        if (data && data.Goods) {
          Report.reset(Multilingual.getText('Tất cả đơn nhập'));
          data.Goods.forEach(i => Report.add(i));

          this.paths = [];
          this.open(Report.root);

          return true;
        }

        return false;

      })); 
    }
  }

  public statistics(field: 'initial' | 'left' | 'gross-value' | 'gross-percentage' | 'discount' | 'exchange' | 'return' | 'export' | 'import'): number {
    switch (field) {
      case 'initial': return this.goods.matches.sum(i => i.base('initial', 'CNY'));
      case 'left': return this.goods.matches.sum(i => i.base('left', 'CNY'));
      case 'gross-value': return this.goods.matches.sum(i => i.gross('total', 'VND'));
      case 'gross-percentage': return this.goods.matches.sum(i => i.gross('total', 'CNY')) / this.goods.matches.sum(i => i.base('profitable', 'CNY'));
      case 'import': return this.goods.matches.sum(i => this.mode == 'worth' ? i.quote('import', 'CNY') - i.quote('provider', 'CNY') : i.Imports.LengthQuantity - i.ProviderLosses.LengthQuantity);
      case 'exchange':

        let cny = this.goods.matches.sum(i => i.gross('total', 'CNY'));
        return cny != 0 ? this.statistics('gross-value') / cny : 0;

      default: return this.goods.matches.sum(i => i.quote(field as any, 'VND'));
    }
  }

  public sum(type: string, field: string = 'Quantity') {
    switch (type) {
      case 'left': return this.goods.matches.sum(i => i.left(field.toLowerCase() as any));
      default:

        switch (field) {
          case 'Quantity': return this.goods.matches.sum(i => i[type].LengthQuantity + i[type].WeightQuantity);
          case 'Count': return this.goods.matches.sum(i => i[type].LengthCount + i[type].WeightCount);
          case 'Discount': return this.goods.matches.sum(i => i[type].LengthDiscount + i[type].WeightDiscount);
          default: return this.goods.matches.sum(i => i[type][field]);
        }
    }
  }

  public open(report: Report) {

    if (report.children.length < 1) return;

    if (!this.paths.contains(report)) this.paths.push(report);
    this.paths = this.paths.take(this.paths.indexOf(report) + 1);
    this.report = report;

    this.goods.source = report.children;
    this.goods.search = '';
  }

  public partner(tag: string): string {
    let t = this.tags.get(tag);
    if (t == null) return '';

    let p = this.partners.get(t.PartnerID);

    return p ? p.Name : '';
  }

  public openStatistics(report: Report, mode: "exports" | "returns") {

    this.sync.with(InventoryReportByImportsComponent).commit(done => {
      switch (mode) {
        case "exports":
          this.ds
            .reports("goods-by-exports", { range: this.range.range, importInvoiceTag: report.Path[0], sku: report.Path.length > 1 ? report.Path[1] : undefined, color: report.Path.length > 2 ? report.Path[2] : undefined })
            .subscribe(reports => {
              done();
              this.modal.show(ExportsViewerComponent, { state: { reports: reports.Goods, range: this.range, tag: report.Path[0], SKU: report.Path.length > 1 ? report.Path[1] : undefined, color: report.Path.length > 2 ? report.Path[2] : undefined } });
            });

          break;

        case "returns":
          this.ds
            .reports("goods-by-returns", { range: this.range.range, importInvoiceTag: report.Path[0], sku: report.Path.length > 1 ? report.Path[1] : undefined, color: report.Path.length > 2 ? report.Path[2] : undefined })
            .subscribe(reports => {
              done();
              this.modal.show(ReturnsViewerComponent, { state: { reports: reports.Goods, range: this.range, tag: report.Path[0], SKU: report.Path.length > 1 ? report.Path[1] : undefined, color: report.Path.length > 2 ? report.Path[2] : undefined } });
            });

          break;

        default:
          done();
          break;

      }
    });    
  }

  ngOnInit(): void {
    main(() => this.layout.title = Multilingual.getText('Báo cáo hàng hóa theo đơn nhập'));
  }

}
